/**
 * OptiBoard search — matches what the board SHOWS.
 *
 * CORE-AS: the previous filter matched `vehicle.vin` and `vehicle.license_plate`, which the board
 * query never selects (and which are encrypted at rest, so loading them would not help), and it
 * did not match the ticket number the row displays (`#TKT-0277`). A user typing the number on the
 * screen found nothing. Identity lookups by VIN or plate belong to customer search, which uses the
 * blind indexes; this filter covers the visible columns only.
 */
type BoardTicket = {
  id?: string | null;
  simple_ticket_number?: number | string | null;
  ticket_number?: number | string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  vehicle?: { year?: number | string | null; make?: string | null; model?: string | null } | null;
  technician?: { name?: string | null } | null;
};

const digitsOnly = (s: string) => s.replace(/\D/g, '');

/** The ticket number as the row displays it, without the `#TKT-` prefix ("0277"). */
export function displayedTicketNumber(ticket: BoardTicket): string | null {
  // NOV3 A6: the repair's number (every day of a multi-day repair shares the head's), as the row shows it.
  const n = (ticket as { repair_number?: number | null }).repair_number ?? ticket.simple_ticket_number ?? ticket.ticket_number;
  return n === null || n === undefined || n === '' ? null : String(n).padStart(4, '0');
}

export function filterOptiBoardTickets<T extends BoardTicket>(tickets: T[], query: string): T[] {
  if (!query || query.trim().length < 2) return tickets;
  const q = query.toLowerCase().trim();
  // "#TKT-0277", "tkt 277", "277" all name the same ticket.
  const numberQuery = q.replace(/^#?\s*(tkt)?[-\s]*/, '');
  const qDigits = digitsOnly(q);

  return tickets.filter((ticket) => {
    const vehicle = ticket.vehicle;
    const vehicleString = vehicle
      ? `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.toLowerCase()
      : '';
    const shown = displayedTicketNumber(ticket);
    // NOV3 A6: the repair's number finds every day of the repair (each row keeps its own id, so opening a result opens
    // that day); the day's own row number still finds that one day (older notes and printouts quote it).
    const own = ticket.simple_ticket_number ?? null;
    const sameNumber = (n: string | number | null | undefined) => n != null && n !== '' && /^\d+$/.test(numberQuery)
      && (String(n).padStart(4, '0') === numberQuery.padStart(4, '0') || String(n).replace(/^0+/, '') === numberQuery.replace(/^0+/, ''));
    const numberMatch = sameNumber(shown) || sameNumber(own);
    // Phone: compare digits so "(555) 123-4567", "555-123-4567" and "5551234567" all match.
    // At least 3 digits, so a short name query with a digit does not match every phone.
    const phoneMatch = qDigits.length >= 3 && !!ticket.customer_phone
      && digitsOnly(ticket.customer_phone).includes(qDigits);

    return (
      numberMatch ||
      !!ticket.id?.toLowerCase().includes(q) ||
      !!ticket.customer_name?.toLowerCase().includes(q) ||
      phoneMatch ||
      vehicleString.includes(q) ||
      !!ticket.technician?.name?.toLowerCase().includes(q)
    );
  });
}
